import {
  CustomerPortalData,
  CustomerPortalRawData,
  CustomerPortalRuleKeys,
} from '@monetize/types/app';
import { ensureBoolean } from '@monetize/utils/core';
import isNil from 'lodash/isNil';

export const CUSTOMER_PORTAL_TABS = {
  OVERVIEW: 'tab-overview',
  INVOICES: 'tab-invoices',
  BILLING_DETAILS: 'tab-billingDetails',
  PAYMENT_METHODS: 'tab-paymentMethods',
} as const;

export type CustomerPortalTabs =
  (typeof CUSTOMER_PORTAL_TABS)[keyof typeof CUSTOMER_PORTAL_TABS];

type ReducedRules = Record<
  CustomerPortalRuleKeys,
  string | number | boolean | string[] | null
>;

function canView(rules: ReducedRules, key: CustomerPortalRuleKeys) {
  return isNil(rules[key]) || rules[key] === 'view' || rules[key] === 'edit';
}

function canEdit(rules: ReducedRules, key: CustomerPortalRuleKeys) {
  return isNil(rules[key]) || rules[key] === 'edit';
}

function canDoBooleanAction(rules: ReducedRules, key: CustomerPortalRuleKeys) {
  return isNil(rules[key]) || ensureBoolean(rules[key]);
}

export const applyRulesToCustomerPortalData = (
  rawData: CustomerPortalRawData,
): CustomerPortalData => {
  const rules = (rawData.rules?.actions || []).reduce(
    (acc, { recordUpdate }) => {
      acc[recordUpdate.targetField] = recordUpdate.value;
      return acc;
    },
    {} as ReducedRules,
  );

  const hasPaymentGateway = !!rawData.billGroup.defaultPaymentGatewayId;

  const output: CustomerPortalData = {
    ...rawData,
    permissions: {
      billingSummary: {
        view: canView(rules, 'billingSummary'),
        edit: true, // set at the end after calculating edit and view for subfields
        purchaseOrderNumber: {
          view: canView(rules, 'purchaseOrderNumber'),
          edit: canEdit(rules, 'purchaseOrderNumber'),
        },
        vatNumber: {
          view: canView(rules, 'vatNumber'),
          edit: canEdit(rules, 'vatNumber'),
        },
        registrationNumber: {
          view: canView(rules, 'registrationNumber'),
          edit: canEdit(rules, 'registrationNumber'),
        },
      },
      contacts: {
        view: canView(rules, 'contacts'),
        edit: canEdit(rules, 'contacts'),
      },
      invoices: {
        view: canView(rules, 'invoices'),
        pay: canDoBooleanAction(rules, 'allowPayment') && hasPaymentGateway,
      },
      paymentMethods: {
        view: canView(rules, 'paymentMethods') && hasPaymentGateway,
        create:
          canDoBooleanAction(rules, 'newPaymentMethod') && hasPaymentGateway,
        changeDefault: {
          view: canView(rules, 'defaultPaymentMethod') && hasPaymentGateway,
          edit: canEdit(rules, 'defaultPaymentMethod') && hasPaymentGateway,
        },
      },
      subscriptions: {
        view: canView(rules, 'subscriptions'),
      },
    },
  };

  const { purchaseOrderNumber, registrationNumber, vatNumber } =
    output.permissions.billingSummary;

  // If UI changes, this might need to be adjusted
  output.permissions.billingSummary.edit =
    output.permissions.billingSummary.view &&
    [purchaseOrderNumber.edit, registrationNumber.edit, vatNumber.edit].some(
      Boolean,
    );

  return output;
};
